import * as React from "react"
import Dialog from "@mui/material/Dialog"
import DialogActions from "@mui/material/DialogActions"
import DialogContent from "@mui/material/DialogContent"
import DialogTitle from "@mui/material/DialogTitle"
import { Box, Checkbox, FormControl, FormControlLabel, IconButton, InputLabel, List, ListItem, ListItemIcon, ListItemText, MenuItem, Select, TextField, Tooltip } from "@mui/material"
import CloseIcon from '@mui/icons-material/Close';
import AddCircleSharpIcon from '@mui/icons-material/AddCircleSharp';
import DeleteIcon from '@mui/icons-material/Delete';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import LockIcon from '@mui/icons-material/Lock';
import RefreshIcon from '@mui/icons-material/Refresh';
import PrintIcon from '@mui/icons-material/Print';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import Grid from "@mui/material/Grid2"
import { Button } from "@mui/material"
import { LoadingBackdropIndeterminate } from "../LoadingBackdrop"
import { AXIOS_API_INSTANCE, LOCATION_INTEGRATIONS_FRIENDLY_NAMES } from "../../Constants"
import { send_authorized_request } from "../../Constants/cognito"
import { useSnackbar } from "notistack"
import { useTrackedUser } from "../UserState"

const supportedIntegrations = ['BLOCKCHYP', 'CARDPOINTE', 'TWILIO', 'MAILGUN', 'ZEBRA_PRINTER', 'PALADIN'].sort()
const defaultLockedAttributes = {
    "BLOCKCHYP_API_KEY": true,
    "BLOCKCHYP_BEARER_TOKEN": true,
    "BLOCKCHYP_SIGNING_KEY": true,
    "TEST": false,
    "ALLOW_EMAIL": false,
    "TERMINAL_NAMES": false,
    "CARDPOINTE_API_KEY": true,
    "CARDPOINTE_MERCHANT_ID": false,
    "CARDPOINTE_TEST": false,
    "TERMINALS": false,
    "TWILIO_ACCOUNT_SID": true,
    "TWILIO_AUTH_TOKEN": true,
    "TWILIO_FROM_PHONE_NUMBER": false,
    "MAILGUN_API_KEY": true,
    "MAILGUN_DOMAIN_NAME": false,
    "MAILGUN_FROM_EMAIL": false,
    "ZEBRA_API_KEY": true,
    "ZEBRA_TENANT": false,
    "ZEBRA_PRINTERS": false,
    "PALADIN_USERNAME": false,
    "PALADIN_PASSWORD": true,
    "PALADIN_STORE_ID": false,
    "PALADIN_DEFAULT_SERVICE_SKU": false,
    "PALADIN_SERVICE_DEPOSIT_SKU": false,
    "PALADIN_CUSTOMER_SYNC_API_KEY": true
}
const defaultUnlockedAttributes = {}
Object.keys(defaultLockedAttributes).forEach((key) => {
    defaultUnlockedAttributes[key] = false
})

export default function LocationIntegrationModal({
    open,
    closeModal,
    location,
    integrationType,
    creatingNew
}) {
    const { enqueueSnackbar } = useSnackbar();
    const [my_user,] = useTrackedUser();
    const [isLoading, setIsLoading] = React.useState(false)
    const [myIntegration, setMyIntegration] = React.useState({})
    const [myIntegrationType, setMyIntegrationType] = React.useState(integrationType ?? '')
    const [newBlockchypTerminalName, setNewBlockchypTerminalName] = React.useState('')
    const [newCardPointeTerminalName, setNewCardPointeTerminalName] = React.useState('')
    const [newZebraPrinterName, setNewZebraPrinterName] = React.useState('')
    const [newZebraPrinterSerialNumber, setNewZebraPrinterSerialNumber] = React.useState('')
    const [lockedAttributes, setLockedAttributes] = React.useState({})

    const handleChange = (prop) => (event) => {
        if (["TEST", "ALLOW_EMAIL", "CARDPOINTE_TEST"].includes(prop)) {
            setMyIntegration({ ...myIntegration, [prop]: event.target.checked })
        } else {
            setMyIntegration({ ...myIntegration, [prop]: event.target.value })
        }
    }
    const changeLockedAttribute = (prop) => {
        lockedAttributes[prop] = !lockedAttributes[prop]
        setLockedAttributes({ ...lockedAttributes })
    }
    const addBlockchypTerminal = () => {
        if (newBlockchypTerminalName.length > 0 && !myIntegration.TERMINAL_NAMES.includes(newBlockchypTerminalName)) {
            setMyIntegration({ ...myIntegration, TERMINAL_NAMES: myIntegration.TERMINAL_NAMES.concat([newBlockchypTerminalName]) })
            setNewBlockchypTerminalName('')
        }
    }
    const removeBlockchypTerminal = (index) => {
        const newMyIntegration = { ...myIntegration }
        newMyIntegration.TERMINAL_NAMES.splice(index, 1)
        setMyIntegration(newMyIntegration)
    }

    const addCardPointeTerminal = () => {
        if (newCardPointeTerminalName.length > 0 && !myIntegration.TERMINALS.includes(newCardPointeTerminalName)) {
            setMyIntegration({ ...myIntegration, TERMINALS: myIntegration.TERMINALS.concat([newCardPointeTerminalName]) })
            setNewCardPointeTerminalName('')
        }
    }
    const removeCardPointeTerminal = (index) => {
        const newMyIntegration = { ...myIntegration }
        newMyIntegration.TERMINALS.splice(index, 1)
        setMyIntegration(newMyIntegration)
    }

    const addZebraPrinter = () => {
        if (newZebraPrinterName.length > 0 && newZebraPrinterSerialNumber.length > 0 && !myIntegration.ZEBRA_PRINTERS.find((x) => x[0] === newZebraPrinterName || x[1] === newZebraPrinterSerialNumber)) {
            const newZebraPrinter = [newZebraPrinterName, newZebraPrinterSerialNumber]
            setMyIntegration({ ...myIntegration, ZEBRA_PRINTERS: myIntegration.ZEBRA_PRINTERS.concat([newZebraPrinter]) })
            setNewZebraPrinterName('')
            setNewZebraPrinterSerialNumber('')
        }
    }
    const removeZebraPrinter = (index) => {
        const newMyIntegration = { ...myIntegration }
        newMyIntegration.ZEBRA_PRINTERS.splice(index, 1)
        setMyIntegration(newMyIntegration)
    }

    const submit = () => {
        const data = {
            integration: myIntegrationType,
            data: { ...myIntegration }
        }
        Object.keys(data.data).forEach((key) => {
            if (lockedAttributes[key]) {
                delete data.data[key]
            }
        })
        setIsLoading(true)
        if (creatingNew) {
            send_authorized_request(() =>
                AXIOS_API_INSTANCE.put(
                    `locations/${location.id}/integrations`,
                    data
                ).then((response) => {
                    enqueueSnackbar('Successfully created location integration', { variant: 'success' })
                    setIsLoading(false)
                    closeModal(response)
                }).catch((error) => {
                    setIsLoading(false)
                    enqueueSnackbar(`Failed to create location integration <${error.response?.data?.message ?? "Unknown Error Occurred"}>`, { variant: 'error' })
                })
            )
        } else {
            send_authorized_request(() =>
                AXIOS_API_INSTANCE.post(
                    `locations/${location.id}/integrations`,
                    data
                ).then((response) => {
                    enqueueSnackbar('Successfully updated location integration', { variant: 'success' })
                    setIsLoading(false)
                    closeModal(response)
                }).catch((error) => {
                    setIsLoading(false)
                    enqueueSnackbar(`Failed to update location integration <${error.response?.data?.message ?? "Unknown Error Occurred"}>`, { variant: 'error' })
                })
            )
        }
    }

    const setNewIntegration = (integration) => {
        setMyIntegrationType(integration)
        if (integration === 'BLOCKCHYP') {
            setMyIntegration({
                BLOCKCHYP_API_KEY: '',
                BLOCKCHYP_BEARER_TOKEN: '',
                BLOCKCHYP_SIGNING_KEY: '',
                TEST: false,
                ALLOW_EMAIL: true,
                TERMINAL_NAMES: []
            })
        } else if (integration === 'CARDPOINTE') {
            setMyIntegration({
                CARDPOINTE_API_KEY: '',
                CARDPOINTE_MERCHANT_ID: '',
                CARDPOINTE_TEST: false,
                TERMINALS: []
            })
        } else if (integration === 'TWILIO') {
            setMyIntegration({
                TWILIO_ACCOUNT_SID: '',
                TWILIO_AUTH_TOKEN: '',
                TWILIO_FROM_PHONE_NUMBER: ''
            })
        } else if (integration === 'MAILGUN') {
            setMyIntegration({
                MAILGUN_API_KEY: '',
                MAILGUN_DOMAIN_NAME: '',
                MAILGUN_FROM_EMAIL: ''
            })
        } else if (integration === 'ZEBRA_PRINTER') {
            setMyIntegration({
                ZEBRA_API_KEY: '',
                ZEBRA_TENANT: '',
                ZEBRA_PRINTERS: []
            })
        } else if (integration === 'PALADIN') {
            setMyIntegration({
                PALADIN_USERNAME: '',
                PALADIN_PASSWORD: '',
                PALADIN_STORE_ID: '',
                PALADIN_DEFAULT_SERVICE_SKU: '',
                PALADIN_SERVICE_DEPOSIT_SKU: '',
                PALADIN_CUSTOMER_SYNC_API_KEY: crypto.randomUUID(),
            })
        }
    }

    React.useEffect(() => {
        if (open) {
            if (creatingNew) {
                setMyIntegration({})
                setMyIntegrationType('')
                setLockedAttributes(structuredClone(defaultUnlockedAttributes))
            } else {
                setMyIntegration(structuredClone(location.integrations[integrationType]))
                setMyIntegrationType(integrationType)
                setLockedAttributes(structuredClone(defaultLockedAttributes))
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open])

    return (
        <div>
            <Dialog
                open={open}
                onClose={() => closeModal && closeModal(false)}
                aria-labelledby="modal-modal-title"
                aria-describedby="modal-modal-description"
                maxWidth='md'
                fullWidth={true}
            >
                <LoadingBackdropIndeterminate
                    open={isLoading}
                    text={"Saving Integration"}
                />
                <DialogTitle>
                    <span style={{ "paddingRight": "24px" }}>{creatingNew ? 'Adding Integration' : `Editing ${integrationType} Integration`}</span>
                    <IconButton
                        aria-label="close"
                        onClick={() => closeModal && closeModal(false)}
                        sx={{
                            position: 'absolute',
                            right: 8,
                            top: 8
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={{ mt: .5 }}>
                    {creatingNew &&
                        <FormControl fullWidth sx={{ mt: .5 }}>
                            <InputLabel id="integration-select-label1">Integration</InputLabel>
                            <Select
                                labelId="integration-select-label1"
                                id="integration-select"
                                value={myIntegrationType}
                                label="Integration"
                                onChange={(e) => setNewIntegration(e.target.value)}
                            >
                                {supportedIntegrations.filter((x) => location.integrations[x] === undefined).map((integration_type) =>
                                    <MenuItem key={integration_type} value={integration_type}>{LOCATION_INTEGRATIONS_FRIENDLY_NAMES[integration_type]}</MenuItem>
                                )}
                            </Select>
                        </FormControl>
                    }
                    {myIntegrationType === 'BLOCKCHYP' &&
                        <Box>
                            <Grid container alignItems={'center'} spacing={1}>
                                <Grid size="grow">
                                    <TextField
                                        margin="dense"
                                        id="BLOCKCHYP_API_KEY"
                                        label="Blockchyp API Key"
                                        type="text"
                                        fullWidth
                                        variant="filled"
                                        value={myIntegration?.BLOCKCHYP_API_KEY || ""}
                                        onChange={handleChange("BLOCKCHYP_API_KEY")}
                                        disabled={lockedAttributes['BLOCKCHYP_API_KEY']}
                                    />
                                </Grid>
                                {!creatingNew &&
                                    <Grid size="auto">
                                        <Button aria-label="search" variant="contained" disabled={!lockedAttributes['BLOCKCHYP_API_KEY']} onClick={() => changeLockedAttribute("BLOCKCHYP_API_KEY")} sx={{ p: 1, minWidth: 1 }}>
                                            {lockedAttributes['BLOCKCHYP_API_KEY'] ? <LockOpenIcon /> : <LockIcon />}
                                        </Button>
                                    </Grid>
                                }
                            </Grid>
                            <Grid container alignItems={'center'} spacing={1}>
                                <Grid size="grow">
                                    <TextField
                                        margin="dense"
                                        id="BLOCKCHYP_BEARER_TOKEN"
                                        label="Blockchyp Bearer Token"
                                        type="text"
                                        fullWidth
                                        variant="filled"
                                        value={myIntegration?.BLOCKCHYP_BEARER_TOKEN || ""}
                                        onChange={handleChange("BLOCKCHYP_BEARER_TOKEN")}
                                        disabled={lockedAttributes['BLOCKCHYP_BEARER_TOKEN']}
                                    />
                                </Grid>
                                {!creatingNew &&
                                    <Grid size="auto">
                                        <Button aria-label="search" variant="contained" disabled={!lockedAttributes['BLOCKCHYP_BEARER_TOKEN']} onClick={() => changeLockedAttribute("BLOCKCHYP_BEARER_TOKEN")} sx={{ p: 1, minWidth: 1 }}>
                                            {lockedAttributes['BLOCKCHYP_BEARER_TOKEN'] ? <LockOpenIcon /> : <LockIcon />}
                                        </Button>
                                    </Grid>
                                }
                            </Grid>
                            <Grid container alignItems={'center'} spacing={1}>
                                <Grid size="grow">
                                    <TextField
                                        margin="dense"
                                        id="BLOCKCHYP_SIGNING_KEY"
                                        label="Blockchyp Signing Key"
                                        type="text"
                                        fullWidth
                                        variant="filled"
                                        value={myIntegration?.BLOCKCHYP_SIGNING_KEY || ""}
                                        onChange={handleChange("BLOCKCHYP_SIGNING_KEY")}
                                        disabled={lockedAttributes['BLOCKCHYP_SIGNING_KEY']}
                                    />
                                </Grid>
                                {!creatingNew &&
                                    <Grid size="auto">
                                        <Button aria-label="search" variant="contained" disabled={!lockedAttributes['BLOCKCHYP_SIGNING_KEY']} onClick={() => changeLockedAttribute("BLOCKCHYP_SIGNING_KEY")} sx={{ p: 1, minWidth: 1 }}>
                                            {lockedAttributes['BLOCKCHYP_SIGNING_KEY'] ? <LockOpenIcon /> : <LockIcon />}
                                        </Button>
                                    </Grid>
                                }
                            </Grid>
                            <Grid container alignItems={'center'} spacing={1}>
                                <Grid size="grow">
                                    <Tooltip
                                        title="Is your blockchyp merchant a test merchant?"
                                        arrow
                                    >
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={myIntegration?.TEST || false}
                                                    onChange={handleChange("TEST")}
                                                    disabled={lockedAttributes['TEST']}
                                                />
                                            }
                                            label="Test Mode"
                                        />
                                    </Tooltip>
                                </Grid>
                            </Grid>
                            <Grid container alignItems={'center'} spacing={1}>
                                <Grid size="grow">
                                    <Tooltip
                                        title="Allow payments via the BlockChyp Online Payment Portal?"
                                        arrow
                                    >
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={myIntegration?.ALLOW_EMAIL || false}
                                                    onChange={handleChange("ALLOW_EMAIL")}
                                                    disabled={lockedAttributes['ALLOW_EMAIL']}
                                                />
                                            }
                                            label="Allow Emailing Payment Links"
                                        />
                                    </Tooltip>
                                </Grid>
                            </Grid>
                            <hr />
                            <Grid container alignItems={'center'} spacing={1}>
                                <Grid>
                                    <TextField
                                        margin="dense"
                                        id="NEW_TERMINAL_NAME"
                                        label="New Terminal Name"
                                        type="text"
                                        fullWidth
                                        variant="filled"
                                        value={newBlockchypTerminalName}
                                        onChange={(event) => setNewBlockchypTerminalName(event.target.value)}
                                        disabled={lockedAttributes['TERMINAL_NAMES']}
                                    />
                                </Grid>
                                <Grid size="auto">
                                    <Button onClick={addBlockchypTerminal} >
                                        <AddCircleSharpIcon />
                                        Add New Terminal
                                    </Button>
                                </Grid>
                            </Grid>
                            <List dense>
                                {(myIntegration?.TERMINAL_NAMES || []).map((item, index) =>
                                    <ListItem
                                        key={item}
                                        secondaryAction={
                                            <IconButton edge="end" aria-label="delete" onClick={() => removeBlockchypTerminal(index)} disabled={lockedAttributes['TERMINAL_NAMES']}>
                                                <DeleteIcon color="error" />
                                            </IconButton>
                                        }
                                    >
                                        <ListItemIcon>
                                            <PointOfSaleIcon />
                                        </ListItemIcon>
                                        <ListItemText
                                            primary={item}
                                        />
                                    </ListItem>
                                )}
                            </List>
                        </Box>}
                    {myIntegrationType === 'CARDPOINTE' &&
                        <Box>
                            <Grid container alignItems={'center'} spacing={1}>
                                <Grid size="grow">
                                    <TextField
                                        margin="dense"
                                        id="CARDPOINTE_API_KEY"
                                        label="CardPointe API Key"
                                        type="text"
                                        fullWidth
                                        variant="filled"
                                        value={myIntegration?.CARDPOINTE_API_KEY || ""}
                                        onChange={handleChange("CARDPOINTE_API_KEY")}
                                        disabled={lockedAttributes['CARDPOINTE_API_KEY']}
                                    />
                                </Grid>
                                {!creatingNew &&
                                    <Grid size="auto">
                                        <Button aria-label="search" variant="contained" disabled={!lockedAttributes['CARDPOINTE_API_KEY']} onClick={() => changeLockedAttribute("CARDPOINTE_API_KEY")} sx={{ p: 1, minWidth: 1 }}>
                                            {lockedAttributes['CARDPOINTE_API_KEY'] ? <LockOpenIcon /> : <LockIcon />}
                                        </Button>
                                    </Grid>
                                }
                            </Grid>
                            <Grid container alignItems={'center'} spacing={1}>
                                <Grid size="grow">
                                    <TextField
                                        margin="dense"
                                        id="CARDPOINTE_MERCHANT_ID"
                                        label="CardPointe Merchant ID"
                                        type="text"
                                        fullWidth
                                        variant="filled"
                                        value={myIntegration?.CARDPOINTE_MERCHANT_ID || ""}
                                        onChange={handleChange("CARDPOINTE_MERCHANT_ID")}
                                        disabled={lockedAttributes['CARDPOINTE_MERCHANT_ID']}
                                    />
                                </Grid>
                            </Grid>
                            <Grid container alignItems={'center'} spacing={1}>
                                <Grid size="grow">
                                    <Tooltip
                                        title="Is your CardPointe in sandbox mode?"
                                        arrow
                                    >
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={myIntegration?.CARDPOINTE_TEST || false}
                                                    onChange={handleChange("CARDPOINTE_TEST")}
                                                    disabled={lockedAttributes['CARDPOINTE_TEST']}
                                                />
                                            }
                                            label="Sandbox Mode"
                                        />
                                    </Tooltip>
                                </Grid>
                            </Grid>
                            <hr />
                            <Grid container alignItems={'center'} spacing={1}>
                                <Grid>
                                    <TextField
                                        margin="dense"
                                        id="NEW_TERMINAL_NAME"
                                        label="New Terminal Name"
                                        type="text"
                                        fullWidth
                                        variant="filled"
                                        value={newCardPointeTerminalName}
                                        onChange={(event) => setNewCardPointeTerminalName(event.target.value)}
                                        disabled={lockedAttributes['TERMINALS']}
                                    />
                                </Grid>
                                <Grid size="auto">
                                    <Button onClick={addCardPointeTerminal} >
                                        <AddCircleSharpIcon />
                                        Add New Terminal
                                    </Button>
                                </Grid>
                            </Grid>
                            <List dense>
                                {(myIntegration?.TERMINALS || []).map((item, index) =>
                                    <ListItem
                                        key={item}
                                        secondaryAction={
                                            <IconButton edge="end" aria-label="delete" onClick={() => removeCardPointeTerminal(index)} disabled={lockedAttributes['TERMINALS']}>
                                                <DeleteIcon color="error" />
                                            </IconButton>
                                        }
                                    >
                                        <ListItemIcon>
                                            <PointOfSaleIcon />
                                        </ListItemIcon>
                                        <ListItemText
                                            primary={item}
                                        />
                                    </ListItem>
                                )}
                            </List>
                        </Box>
                    }
                    {myIntegrationType === 'TWILIO' &&
                        <Box>
                            <Grid container alignItems={'center'} spacing={1}>
                                <Grid size="grow">
                                    <TextField
                                        margin="dense"
                                        id="TWILIO_ACCOUNT_SID"
                                        label="Twilio Account SID"
                                        type="text"
                                        fullWidth
                                        variant="filled"
                                        value={myIntegration?.TWILIO_ACCOUNT_SID || ""}
                                        onChange={handleChange("TWILIO_ACCOUNT_SID")}
                                        disabled={lockedAttributes['TWILIO_ACCOUNT_SID']}
                                    />
                                </Grid>
                                {!creatingNew &&
                                    <Grid size="auto">
                                        <Button aria-label="search" variant="contained" disabled={!lockedAttributes['TWILIO_ACCOUNT_SID']} onClick={() => changeLockedAttribute("TWILIO_ACCOUNT_SID")} sx={{ p: 1, minWidth: 1 }}>
                                            {lockedAttributes['TWILIO_ACCOUNT_SID'] ? <LockOpenIcon /> : <LockIcon />}
                                        </Button>
                                    </Grid>
                                }
                            </Grid>
                            <Grid container alignItems={'center'} spacing={1}>
                                <Grid size="grow">
                                    <TextField
                                        margin="dense"
                                        id="TWILIO_AUTH_TOKEN"
                                        label="Twilio Auth Token"
                                        type="text"
                                        fullWidth
                                        variant="filled"
                                        value={myIntegration?.TWILIO_AUTH_TOKEN || ""}
                                        onChange={handleChange("TWILIO_AUTH_TOKEN")}
                                        disabled={lockedAttributes['TWILIO_AUTH_TOKEN']}
                                    />
                                </Grid>
                                {!creatingNew &&
                                    <Grid size="auto">
                                        <Button aria-label="search" variant="contained" disabled={!lockedAttributes['TWILIO_AUTH_TOKEN']} onClick={() => changeLockedAttribute("TWILIO_AUTH_TOKEN")} sx={{ p: 1, minWidth: 1 }}>
                                            {lockedAttributes['TWILIO_AUTH_TOKEN'] ? <LockOpenIcon /> : <LockIcon />}
                                        </Button>
                                    </Grid>
                                }
                            </Grid>
                            <Grid container alignItems={'center'} spacing={1}>
                                <Grid size="grow">
                                    <TextField
                                        margin="dense"
                                        id="TWILIO_FROM_PHONE_NUMBER"
                                        label="FROM Phone Number"
                                        type="text"
                                        fullWidth
                                        variant="filled"
                                        value={myIntegration?.TWILIO_FROM_PHONE_NUMBER || ""}
                                        onChange={handleChange("TWILIO_FROM_PHONE_NUMBER")}
                                        helperText={"Include the +1"}
                                        disabled={lockedAttributes['TWILIO_FROM_PHONE_NUMBER']}
                                    />
                                </Grid>
                            </Grid>
                        </Box>}
                    {myIntegrationType === 'MAILGUN' &&
                        <Box>
                            <Grid container alignItems={'center'} spacing={1} sx={{ width: '100%' }}>
                                <Grid size="grow">
                                    <TextField
                                        margin="dense"
                                        id="MAILGUN_API_KEY"
                                        label="MailGun API Key"
                                        type="text"
                                        fullWidth
                                        variant="filled"
                                        value={myIntegration?.MAILGUN_API_KEY || ""}
                                        onChange={handleChange("MAILGUN_API_KEY")}
                                        disabled={lockedAttributes['MAILGUN_API_KEY']}
                                    />
                                </Grid>
                                {!creatingNew &&
                                    <Grid size="auto">
                                        <Button aria-label="search" variant="contained" disabled={!lockedAttributes['MAILGUN_API_KEY']} onClick={() => changeLockedAttribute("MAILGUN_API_KEY")} sx={{ p: 1, minWidth: 1 }}>
                                            {lockedAttributes['MAILGUN_API_KEY'] ? <LockOpenIcon /> : <LockIcon />}
                                        </Button>
                                    </Grid>
                                }
                            </Grid>
                            <Grid container alignItems={'center'} spacing={1}>
                                <Grid size="grow">
                                    <TextField
                                        margin="dense"
                                        id="MAILGUN_DOMAIN_NAME"
                                        label="MailGun Domain Name"
                                        type="text"
                                        fullWidth
                                        variant="filled"
                                        value={myIntegration?.MAILGUN_DOMAIN_NAME || ""}
                                        onChange={handleChange("MAILGUN_DOMAIN_NAME")}
                                        disabled={lockedAttributes['MAILGUN_DOMAIN_NAME']}
                                    />
                                </Grid>
                            </Grid>
                            <Grid container alignItems={'center'} spacing={1}>
                                <Grid size="grow">
                                    <TextField
                                        margin="dense"
                                        id="MAILGUN_FROM_EMAIL"
                                        label="FROM Email Address"
                                        type="text"
                                        fullWidth
                                        variant="filled"
                                        value={myIntegration?.MAILGUN_FROM_EMAIL || ""}
                                        onChange={handleChange("MAILGUN_FROM_EMAIL")}
                                        disabled={lockedAttributes['MAILGUN_FROM_EMAIL']}
                                    />
                                </Grid>
                            </Grid>
                        </Box>}
                    {myIntegrationType === 'ZEBRA_PRINTER' &&
                        <Box>
                            <Grid container alignItems={'center'} spacing={1} sx={{ width: '100%' }}>
                                <Grid size="grow">
                                    <TextField
                                        margin="dense"
                                        id="ZEBRA_API_KEY"
                                        label="API Key for Zebra cloud"
                                        type="text"
                                        fullWidth
                                        variant="filled"
                                        value={myIntegration?.ZEBRA_API_KEY || ""}
                                        onChange={handleChange("ZEBRA_API_KEY")}
                                        disabled={lockedAttributes['ZEBRA_API_KEY']}
                                    />
                                </Grid>
                                {!creatingNew &&
                                    <Grid size="auto">
                                        <Button aria-label="search" variant="contained" disabled={!lockedAttributes['ZEBRA_API_KEY']} onClick={() => changeLockedAttribute("ZEBRA_API_KEY")} sx={{ p: 1, minWidth: 1 }}>
                                            {lockedAttributes['ZEBRA_API_KEY'] ? <LockOpenIcon /> : <LockIcon />}
                                        </Button>
                                    </Grid>
                                }
                            </Grid>
                            <Grid container alignItems={'center'} spacing={1} sx={{ width: '100%' }}>
                                <Grid size="grow">
                                    <TextField
                                        margin="dense"
                                        id="ZEBRA_TENANT"
                                        label="Tenant of your Zebra Cloud provider"
                                        type="text"
                                        fullWidth
                                        variant="filled"
                                        value={myIntegration?.ZEBRA_TENANT || ""}
                                        onChange={handleChange("ZEBRA_TENANT")}
                                        disabled={lockedAttributes['ZEBRA_TENANT']}
                                    />
                                </Grid>
                                {!creatingNew &&
                                    <Grid size="auto">
                                        <Button aria-label="search" variant="contained" disabled={!lockedAttributes['ZEBRA_TENANT']} onClick={() => changeLockedAttribute("ZEBRA_TENANT")} sx={{ p: 1, minWidth: 1 }}>
                                            {lockedAttributes['ZEBRA_TENANT'] ? <LockOpenIcon /> : <LockIcon />}
                                        </Button>
                                    </Grid>
                                }
                            </Grid>
                            <Grid container alignItems={'center'} spacing={1}>
                                <Grid>
                                    <TextField
                                        margin="dense"
                                        id="NEW_ZEBRA_PRINTER_NAME"
                                        label="New Printer Name"
                                        type="text"
                                        fullWidth
                                        variant="filled"
                                        value={newZebraPrinterName}
                                        onChange={(event) => setNewZebraPrinterName(event.target.value)}
                                        disabled={lockedAttributes['ZEBRA_PRINTERS']}
                                    />
                                </Grid>
                                <Grid>
                                    <TextField
                                        margin="dense"
                                        id="NEW_ZEBRA_PRINTER_SERIAL_NUMBER"
                                        label="Serial Number"
                                        type="text"
                                        fullWidth
                                        variant="filled"
                                        value={newZebraPrinterSerialNumber}
                                        onChange={(event) => setNewZebraPrinterSerialNumber(event.target.value)}
                                        disabled={lockedAttributes['ZEBRA_PRINTERS']}
                                    />
                                </Grid>
                                <Grid size="auto">
                                    <Button onClick={addZebraPrinter}>
                                        <AddCircleSharpIcon />
                                        Add New Terminal
                                    </Button>
                                </Grid>
                            </Grid>
                            <List dense>
                                {(myIntegration?.ZEBRA_PRINTERS || []).length === 0 &&
                                    <Box sx={{ pt: 1, textAlign: 'center' }}>
                                        <span>- No Printers -</span>
                                    </Box>
                                }
                                {(myIntegration?.ZEBRA_PRINTERS || []).map((item, index) =>
                                    <ListItem
                                        key={item}
                                        secondaryAction={
                                            <IconButton edge="end" aria-label="delete" onClick={() => removeZebraPrinter(index)} disabled={lockedAttributes['ZEBRA_PRINTERS']}>
                                                <DeleteIcon color="error" />
                                            </IconButton>
                                        }
                                    >
                                        <ListItemIcon>
                                            <PrintIcon />
                                        </ListItemIcon>
                                        <ListItemText
                                            primary={item[0]}
                                            secondary={item[1]}
                                        />
                                    </ListItem>
                                )}
                            </List>
                        </Box>}
                    {myIntegrationType === 'PALADIN' &&
                        <Box>
                            <Grid container alignItems={'center'} spacing={1} sx={{ width: '100%' }}>
                                <Grid size="grow">
                                    <TextField
                                        margin="dense"
                                        id="PALADIN_USERNAME"
                                        label="Paladin Ecommerce Username"
                                        type="text"
                                        fullWidth
                                        variant="filled"
                                        value={myIntegration?.PALADIN_USERNAME ?? ""}
                                        onChange={handleChange("PALADIN_USERNAME")}
                                        disabled={lockedAttributes['PALADIN_USERNAME']}
                                    />
                                </Grid>
                            </Grid>
                            <Grid container alignItems={'center'} spacing={1} sx={{ width: '100%' }}>
                                <Grid size="grow">
                                    <TextField
                                        margin="dense"
                                        id="PALADIN_PASSWORD"
                                        label="Paladin Ecommerce Password"
                                        type="text"
                                        fullWidth
                                        variant="filled"
                                        value={myIntegration?.PALADIN_PASSWORD ?? ""}
                                        onChange={handleChange("PALADIN_PASSWORD")}
                                        disabled={lockedAttributes['PALADIN_PASSWORD']}
                                    />
                                </Grid>
                                {!creatingNew &&
                                    <Grid size="auto">
                                        <Button aria-label="search" variant="contained" disabled={!lockedAttributes['PALADIN_PASSWORD']} onClick={() => changeLockedAttribute("PALADIN_PASSWORD")} sx={{ p: 1, minWidth: 1 }}>
                                            {lockedAttributes['PALADIN_PASSWORD'] ? <LockOpenIcon /> : <LockIcon />}
                                        </Button>
                                    </Grid>
                                }
                            </Grid>
                            <Grid container alignItems={'center'} spacing={1} sx={{ width: '100%' }}>
                                <Grid size="grow">
                                    <TextField
                                        margin="dense"
                                        id="PALADIN_CUSTOMER_SYNC_API_KEY"
                                        label="Paladin Customer Sync API Key"
                                        type="text"
                                        fullWidth
                                        variant="filled"
                                        value={myIntegration?.PALADIN_CUSTOMER_SYNC_API_KEY ?? ""}
                                        disabled
                                    />
                                </Grid>
                                <Grid size="auto">
                                    <Button aria-label="search" variant="contained" disabled={lockedAttributes['PALADIN_CUSTOMER_SYNC_API_KEY']} onClick={() => setMyIntegration({ ...myIntegration, PALADIN_CUSTOMER_SYNC_API_KEY: crypto.randomUUID() })} sx={{ p: 1, minWidth: 1 }}>
                                        <RefreshIcon />
                                    </Button>
                                </Grid>
                                {!creatingNew &&
                                    <Grid size="auto">
                                        <Button aria-label="search" variant="contained" disabled={!lockedAttributes['PALADIN_CUSTOMER_SYNC_API_KEY']} onClick={() => changeLockedAttribute("PALADIN_CUSTOMER_SYNC_API_KEY")} sx={{ p: 1, minWidth: 1 }}>
                                            {lockedAttributes['PALADIN_CUSTOMER_SYNC_API_KEY'] ? <LockOpenIcon /> : <LockIcon />}
                                        </Button>
                                    </Grid>
                                }
                                <Grid size={12}><span>URL: https://api.ProfitMaxProSuite.com/{my_user.client.id}/customers/paladin_sync?location_id={location.id}</span></Grid>
                            </Grid>
                            <Grid container alignItems={'center'} spacing={1} sx={{ width: '100%' }}>
                                <Grid size="grow">
                                    <TextField
                                        margin="dense"
                                        id="PALADIN_STORE_ID"
                                        label="Paladin Ecommerce Store ID"
                                        type="text"
                                        fullWidth
                                        variant="filled"
                                        value={myIntegration?.PALADIN_STORE_ID ?? ""}
                                        onChange={handleChange("PALADIN_STORE_ID")}
                                        disabled={lockedAttributes['PALADIN_STORE_ID']}
                                    />
                                </Grid>
                            </Grid>
                            <Grid container alignItems={'center'} spacing={1} sx={{ width: '100%' }}>
                                <Grid size="grow">
                                    <TextField
                                        margin="dense"
                                        id="PALADIN_DEFAULT_SERVICE_SKU"
                                        label="Paladin Ecommerce Default Service SKU"
                                        type="text"
                                        fullWidth
                                        variant="filled"
                                        value={myIntegration?.PALADIN_DEFAULT_SERVICE_SKU ?? ""}
                                        onChange={handleChange("PALADIN_DEFAULT_SERVICE_SKU")}
                                        disabled={lockedAttributes['PALADIN_DEFAULT_SERVICE_SKU']}
                                    />
                                </Grid>
                            </Grid>
                            <Grid container alignItems={'center'} spacing={1} sx={{ width: '100%' }}>
                                <Grid size="grow">
                                    <TextField
                                        margin="dense"
                                        id="PALADIN_SERVICE_DEPOSIT_SKU"
                                        label="Paladin Ecommerce Service Deposit SKU"
                                        type="text"
                                        fullWidth
                                        variant="filled"
                                        value={myIntegration?.PALADIN_SERVICE_DEPOSIT_SKU ?? ""}
                                        onChange={handleChange("PALADIN_SERVICE_DEPOSIT_SKU")}
                                        disabled={lockedAttributes['PALADIN_SERVICE_DEPOSIT_SKU']}
                                    />
                                </Grid>
                            </Grid>
                        </Box>}
                </DialogContent>
                <DialogActions>
                    <Grid container justifyContent="space-between" sx={{ padding: "0px 30px 10px 30px", width: '100%' }}>
                        <Grid size="auto">
                        </Grid>
                        <Grid size="auto">
                            <Button variant="contained" type="submit" onClick={submit}>
                                Submit
                            </Button>
                        </Grid>
                    </Grid>
                </DialogActions>
            </Dialog >
        </div >
    )
}
